import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * This pipe scans text for specific Unicode characters and replaces them with HTML <span> elements
 * using the correct custom font classes (e.g., Wingdings, Helvetica, ATA Dingbats).
 *
 * - It uses a Map to define pairs of [Unicode character → HTML replacement].
 * - Each entry in the Map is processed with a global regex to replace all occurrences in the text.
 * - A special case handles the "+" sign: it's only replaced if it precedes a number, to avoid breaking
 *   normal text that uses "+" (like "A + B"). e.g., "+71002": it's the 'triangle with a dot inside'
 *   symbol before the Article number.
 *
 * The final HTML is sanitized with DomSanitizer to allow safe binding with [innerHTML].
 */
@Pipe({
  name: 'SpecialGlyphs',
  standalone: true
})
export class SpecialGlyphsPipe implements PipeTransform {

  constructor(private sanitizer: DomSanitizer) {}

  transform(value: string): SafeHtml {
    if (!value) return '';

    let result = value;

    // Replaces only the "+" plus sign followed by a digit
    // ATA Dingbats Part A characters
    result = result.replace(/\+(?=\d)/g, '<span class="ata-dingbats-a">+</span>');

    // map: original character -> substitute character + class
    const map = new Map<string, string>([
      // Wingdings Font-family characters
      ['\uF049', '<span class="wingding">I</span>'], //  → (U+F049 → Hand)
      // Helvetica Font-family characters
      ['\uF054', '<span class="helvetica">⮽</span>'], // ⮽ → (U+2612 → Ballot Box with X)
      ['\uF048', '<span class="helvetica">★</span>'], // ★ → (U+2605 → Black Star)
      ['\uF04A', '<span class="helvetica">✪</span>'], // ✪ → (U+272A → Circled White Star)
      ['\uF075', '<span class="helvetica">⯈</span>'], // ⯈ → (U+2BC8 → Rightward Arrow)
    ]);

    map.forEach((replacement, targetChar) => {
      const regex = new RegExp(targetChar, 'g');
      result = result.replace(regex, replacement);
    });

    return this.sanitizer.bypassSecurityTrustHtml(result);
  }

}
